import { Box, Flex } from '@chakra-ui/react';
import { useRouterState } from '@tanstack/react-router';

import { DiscordAdminFooter } from '#features/discord-lfg-admin/components/DiscordAdminFooter';
import { DiscordAdminHeader } from '#features/discord-lfg-admin/components/DiscordAdminHeader';
import { AppFooter } from '#features/layout/components/AppFooter';
import { useIsChatThread } from '#hooks/useIsChatThread';
import { useTypingFieldFocused } from '#hooks/useTypingFieldFocused';
import { mobileScreen } from '../../model/mobileNavigation';
import { fitWindowHeight, isChatListScreen, phoneBottomReserve } from '../../model/pageHeight';
import { BottomNavigation } from '../BottomNavigation';
import { Header } from '../Header';

/** Прежний пол высоты содержимого: окно минус шапка и верхний зазор. */
const PAGE_FLOOR = 'calc(100vh - 80px)';

type MainLayoutProps = {
  children: React.ReactNode;
  /**
   * Обычная страница сайта, что бы ни лежало в совпавших маршрутах.
   *
   * Нужно экрану «страницы нет»: путь вроде `/nope` остаётся совпавшим с лендингом, и тот
   * забирает себе и шапку, и подвал — лендинг рисует свой собственный, поэтому глобальный
   * подавляется, и страница ошибки оставалась без подвала и с маркетинговой навигацией.
   */
  plain?: boolean;
};

export const MainLayout = ({ children, plain = false }: MainLayoutProps) => {
  const isLandingMatch = useRouterState({
    select: (state) =>
      state.matches?.some((match) => match?.routeId?.includes('/_landing')) ?? false,
  });
  const isLandingPage = !plain && isLandingMatch;

  // Экран переписки, а не адрес: см. `useIsChatThread`.
  const isChatThread = useIsChatThread();
  const isTask = useRouterState({
    select: (state) => mobileScreen(state.location.pathname).kind === 'task',
  });
  const isChatList = useRouterState({
    select: (state) => isChatListScreen(state.location.pathname, isChatThread),
  });
  const isMainSection = useRouterState({
    select: (state) => {
      const screen = mobileScreen(state.location.pathname);
      return (
        (screen.kind === 'brand' && screen.root === 'home') ||
        (screen.kind === 'root' && screen.root !== 'settings')
      );
    },
  });

  // The Discord server-owner admin gets its own slim header (logo + account) instead of
  // the consumer teamplay.gg one — the app nav (notifications, create post, dashboard
  // settings) is irrelevant there. Matched by routeId like isLandingPage/isChatThread.
  const isDiscordAdmin = useRouterState({
    select: (state) =>
      state.matches?.some((match) => match?.routeId?.includes('/discord-lfg-bot/admin')) ?? false,
  });

  // The landing renders its own footer inside its content grid (so it inherits the
  // landing-scoped width + contrast overrides), so the global footer is suppressed
  // there to avoid a duplicate. Driven by `staticData.rendersOwnFooter` on the route
  // (not a path match), so it keeps working when the landing moves to prod routes.
  const ownFooterMatch = useRouterState({
    select: (state) => state.matches?.some((match) => match?.staticData?.rendersOwnFooter) ?? false,
  });
  const rendersOwnFooter = !plain && ownFooterMatch;

  // The LFG bot landing renders its own branded header component, so it flags
  // `staticData.rendersOwnHeader` to suppress the global one entirely (no stacked headers).
  const ownHeaderMatch = useRouterState({
    select: (state) => state.matches?.some((match) => match?.staticData?.rendersOwnHeader) ?? false,
  });
  const rendersOwnHeader = !plain && ownHeaderMatch;

  // Pages with their own marketing chrome (rendersOwnHeader) also hide the app bottom
  // nav: they carry their own sticky mobile CTA, which would overlap/cover it.
  const hideBottomNav =
    isLandingPage || isChatThread || isDiscordAdmin || rendersOwnHeader || isTask;
  /*
    Пока открыта клавиатура, навигация прячется (02.10.2026). В приложении клавиатура сжимает
    вебвью, и панель, прибитая к низу, въезжала прямо на поле комментария и выпадашки создания
    заявки, отнимая ~70px из ~300 над клавиатурой. Разделы во время набора никто не переключает.
    Прячем мгновенно, без анимации: клавиатура едет сама, два движения сразу читаются как рывок.
    Вместе с панелью уходит и оставленное под неё место внизу страницы.
  */
  const typing = useTypingFieldFocused();
  const bottomNavAway = hideBottomNav || typing;
  const bottomReserve = phoneBottomReserve(bottomNavAway);
  const chatListHeight = fitWindowHeight(bottomReserve);

  return (
    <Flex
      w="100%"
      minH={{ base: isChatThread ? 0 : '100vh', lg: '100vh' }}
      position={{ base: isChatThread ? 'fixed' : 'static', lg: 'static' }}
      inset={{ base: isChatThread ? 0 : 'auto', lg: 'auto' }}
      overflow={{ base: isChatThread ? 'hidden' : 'visible', lg: 'visible' }}
      // The thread replaces the global Header, including its notch/status-bar inset.
      // Keep it outside the thread's measured header and message scroll coordinates.
      css={
        isChatThread
          ? {
              '@media (max-width: 63.999rem)': {
                'html[data-native-platform="ios"] &': {
                  paddingTop: 'env(safe-area-inset-top, 0px)',
                },
              },
            }
          : undefined
      }
      flexDir="column"
      m="0 auto"
    >
      {/*
        Узкий экран переписки шапку приложения не рисует. Тред и так несёт свою панель — стрелка
        «назад», имя собеседника, звонок, меню, — а общая шапка над ней дублирует навигацию,
        к разговору не относящуюся, и вместе с ней съедала около трети высоты экрана.
        Прячем тем же порогом, что и остальное поведение треда (`lg`): от 1024 px переписка
        живёт внутри дашборда рядом со списком чатов, и шапка там на своём месте.
        Скрываем стилем, а не условием: значение брейкпоинта на сервере неизвестно, и ветвление
        в разметке разъехалось бы с тем, что отрисует браузер.
      */}
      {!rendersOwnHeader && (
        <Box
          w="100%"
          display={{ base: isChatThread ? 'none' : 'block', lg: 'block' }}
          /*
            Липкость живёт здесь, на обёртке, а не только внутри самой шапки.

            Обе шапки объявляют `position: sticky; top: 0`, но липнуть им было негде: обёртка
            ровно своей высоты, и sticky-элемент вместе с ней уезжал за верхний край на первом
            же обороте колеса. Меню дашборда и панель чата при этом остаются на `top: 80px`
            (шапка 60 + зазор 20) — отсюда и вид «меню отклеилось от шапки»: меню стояло, а
            шапки над ним уже не было.

            В мобильном вебе липкость остаётся только от `md`. В iOS-приложении игроку нужны
            постоянные верхние действия, поэтому обычную шапку закрепляем и на телефоне.
          */
          position={{ base: isLandingPage ? 'static' : 'sticky', md: 'sticky' }}
          css={
            !isLandingPage && !isDiscordAdmin && !rendersOwnFooter
              ? { 'html[data-native-platform="ios"] &': { position: 'sticky' } }
              : undefined
          }
          top="0"
          zIndex="sticky"
        >
          {isDiscordAdmin ? <DiscordAdminHeader /> : <Header />}
        </Box>
      )}
      <Flex
        as="main"
        alignItems="stretch"
        position="relative"
        flexDirection="column"
        flex="1"
        minH={0}
        // Полоса разговора занимает низ экрана наравне с навигацией, поэтому и место под неё
        // страница оставляет так же — иначе она ложится на то, что оказалось внизу: кнопку,
        // последнюю карточку ленты, футер формы.
        //
        // Именно `max`, а не сумма: полоса стоит НАД навигацией и в занятое ею место навигацию уже
        // включает. Сложение отсчитало бы навигацию и безопасную зону дважды и оторвало бы страницу
        // от нижнего края на высоту меню.
        pb={{ base: bottomReserve, md: 0 }}
      >
        <Flex
          px={{ base: isChatThread ? 0 : 3, lg: 5 }}
          py={{ base: isChatThread ? 0 : 5, lg: 5 }}
          pt={{ base: isChatThread ? 0 : isMainSection ? 3 : 5, lg: 5 }}
          mx="auto"
          mb={{ base: isChatThread ? 0 : 'auto', lg: 'auto' }}
          w="100%"
          maxW="1800px"
          /*
            На широком экране высоту задаёт не пол, а флекс: корень держит `minH: 100vh`, main
            растягивается, и футер и так стоит у нижнего края. Пол `calc(100vh - 80px)` при
            шапке 60 px и подвале 89 px давал документ на 69 px выше экрана — страница ездила
            под ногами на всех разделах, а на переписке, где скроллить вообще нечего, это
            выглядело как «подложка гуляет».
          */
          /*
            Список чатов на телефоне - ровно окно, лишнее листается внутри (`fitWindowHeight`).
            От `md` нижней навигации нет, и список возвращается к обычной странице с подвалом.
          */
          minH={{
            base: isChatThread ? 0 : isChatList ? chatListHeight : PAGE_FLOOR,
            md: isChatList ? PAGE_FLOOR : undefined,
            lg: 'auto',
          }}
          maxH={{
            base: isChatList ? chatListHeight : undefined,
            md: isChatList ? 'none' : undefined,
          }}
          display="flex"
          flexDir="column"
          alignItems="center"
          flex="1"
          overflowY={{
            base: isChatThread || isChatList ? 'hidden' : 'visible',
            md: isChatList ? 'visible' : undefined,
            lg: 'visible',
          }}
          overflowX="clip"
        >
          {children}
        </Flex>
        {isDiscordAdmin ? (
          <DiscordAdminFooter />
        ) : (
          !rendersOwnFooter && (
            <Box
              display={{
                base: isChatThread || isChatList ? 'none' : 'block',
                md: isChatList ? 'block' : undefined,
                lg: 'block',
              }}
              // Позиционирование здесь не ради смещения, а ради порядка отрисовки: страницы
              // дашборда полны sticky-блоков (левое меню, календарь сессий), а позиционированное
              // всегда рисуется поверх обычного потока — независимо от порядка в разметке. Без
              // этой строки любой sticky-сосед ложится на футер, стоит странице стать длиннее.
              position="relative"
              w="100%"
              maxW={{ base: '100%', md: '1200px' }}
              mx="auto"
              px={{ base: 4, md: 6, lg: 8 }}
            >
              <AppFooter />
            </Box>
          )
        )}
      </Flex>
      {!hideBottomNav && <BottomNavigation hidden={typing} />}
    </Flex>
  );
};
